import React, { useState, useEffect, useCallback } from 'react';
import { Coins, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';
import { creditsService } from '../../services/creditsService';

interface UserCreditsProps {
  credits?: number;
  className?: string;
  showIcon?: boolean;
  showTrend?: boolean;
  trendValue?: number;
  size?: 'sm' | 'md' | 'lg';
  autoRefresh?: boolean;
  refreshInterval?: number;
}

const UserCredits: React.FC<UserCreditsProps> = ({
  credits: propCredits,
  className,
  showIcon = true,
  showTrend = false,
  trendValue = 0,
  size = 'lg',
  autoRefresh = false,
  refreshInterval = 30000 // 30 segundos
}) => {
  const [credits, setCredits] = useState<number>(propCredits || 0);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  // Carregar créditos do servidor se não fornecidos via props
  const loadCredits = useCallback(async () => {
    if (propCredits !== undefined) return; // Se créditos vêm via props, não carregar do servidor
    
    try {
      setIsLoading(true);
      const creditsData = await creditsService.getCredits();
      setCredits(creditsData.credits);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Erro ao carregar créditos:', error);
    } finally {
      setIsLoading(false);
    }
  }, [propCredits]);

  // Carregar créditos na montagem do componente
  useEffect(() => {
    if (propCredits === undefined) {
      loadCredits();
    } else {
      setCredits(propCredits);
    }
  }, [propCredits, loadCredits]);

  // Auto-refresh se habilitado
  useEffect(() => {
    if (autoRefresh && propCredits === undefined) {
      const interval = setInterval(loadCredits, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, propCredits, loadCredits]);

  // Atualizar créditos quando props mudarem
  useEffect(() => {
    if (propCredits !== undefined) {
      setCredits(propCredits);
    }
  }, [propCredits]);

  const formatCredits = (value: number): string => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } 
    return value.toLocaleString('pt-BR');
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'text-xs px-2 py-1';
      case 'lg':
        return 'text-lg px-4 py-2';
      default:
        return 'text-sm px-3 py-1.5';
    }
  };

  const getTrendIcon = () => {
    if (trendValue > 0) {
      return <TrendingUp className="w-3 h-3 text-green-400" />;
    } else if (trendValue < 0) {
      return <TrendingDown className="w-3 h-3 text-red-400" />;
    }
    return null;
  };

  const getCreditsColor = () => {
    if (credits >= 10000) return 'text-green-400';
    if (credits >= 1000) return 'text-yellow-400';
    if (credits >= 100) return 'text-orange-400';
    return 'text-red-400';
  };

  return (
    <div
      className={cn(
        'flex items-center space-x-2 rounded-lg border-2 border-white/20 bg-white/10 backdrop-blur-sm transition-all duration-200 hover:bg-white/20',
        getSizeClasses(),
        className
      )}
    >
      {showIcon && (
        <Coins className="w-4 h-4 text-yellow-400" />
      )}
      
      <div className="flex flex-col flex-1">
        <div className="flex items-center justify-between">
          <span className="text-white/80 text-xl font-medium">
            Créditos
          </span>
          {propCredits === undefined && (
            <button
              onClick={loadCredits}
              disabled={isLoading}
              className="text-white/60 hover:text-white/80 transition-colors"
              title="Atualizar créditos"
            >
              <RefreshCw className={cn(
                'w-3 h-3',
                isLoading && 'animate-spin'
              )} />
            </button>
          )}
        </div>
        <div className="flex items-center space-x-1">
          <span className={cn('text-xl font-bold', getCreditsColor())}>
            {isLoading ? '...' : formatCredits(credits)}
          </span>
          {showTrend && trendValue !== 0 && (
            <div className="flex items-center space-x-1">
              {getTrendIcon()}
              <span className={cn(
                'text-xl font-bold',
                trendValue > 0 ? 'text-green-400' : 'text-red-400'
              )}>
                {Math.abs(trendValue)}
              </span>
            </div>
          )}
        </div>
        {propCredits === undefined && (
          <span className="text-white/40 text-xs">
            Atualizado: {lastUpdate.toLocaleTimeString()}
          </span>
        )}
      </div>
    </div>
  );
};

export default UserCredits;